/* eslint-disable no-param-reassign */
/**
 * Licent hypotheken hub
 *
 * @author Robuust
 * @author Joost Waaijer <joost@robuust.digital>
 */

import { Controller } from '@hotwired/stimulus';

/**
 * Controller for mortgage calculation.
 */
export default class extends Controller {
  static targets = [
    'form', 'result', 'mortgageText', 'monthlyCostGrossText', 'interestRateText',
    'personOneInput', 'personTwo', 'personTwoInput', 'personOneInputIncome',
    'personTwoInputIncome', 'stepOne', 'monthlyIncomeLabel', 'submitButton',
  ];

  static values = {
    incomeType: { type: String, default: 'month' },
  };

  currencyFormat = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });

  interestFormat = new Intl.NumberFormat('nl-NL', { style: 'percent', minimumFractionDigits: 2 });

  monthlyCostsGross = 0;

  monthlyCostsNett = 0;

  interestRate = 0;

  mortgage = 0;

  formId = 0;

  holidayAllowance = 1.08;

  modalController = null;

  connect() {
    this.formId = `form-${Math.random().toString(36).slice(2)}`;
    this.element.dataset.formId = this.formId;

    const modal = document.getElementById('modal-hubspot');
    this.modalController = this.application.getControllerForElementAndIdentifier(modal, 'modal');
  }

  /**
   * Calculate monthly costs
   *
   * @param {Event} e
   */
  async submitForm(e) {
    if (!this.validateForm(this.formTarget)) {
      return;
    }
    e.preventDefault();

    const [arrow, loader] = this.submitButtonTarget.children;
    arrow.classList.add('hidden');
    loader.classList.remove('hidden');

    const response = await fetch(window.location.href, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
      body: new FormData(this.formTarget),
    });
    const result = await response.json();

    this.resultTargets.forEach((r) => r.classList.remove('hidden'));
    this.stepOneTargets.forEach((r) => r.classList.add('hidden'));
    this.scrollToCalculator();

    this.mortgage = result.data.result.no_nhg.max_mortgage;
    this.mortgageTextTarget.innerHTML = this.currencyFormat.format(this.mortgage);
    this.monthlyCostsGross = result.data.result.no_nhg.first_month_gross_charge;
    this.monthlyCostGrossTextTarget.innerHTML = this.currencyFormat.format(this.monthlyCostsGross);
    this.monthlyCostsNett = result.data.result.no_nhg.first_month_nett_charge;
    this.interestRate = result.meta.calculationValues.interest_rate_no_nhg / 100;
    this.interestRateTextTarget.innerHTML = this.interestFormat.format(this.interestRate);

    if (window.dataLayer) {
      window.dataLayer.push({ event: 'hypotheek_berekening' });
    }

    arrow.classList.remove('hidden');
    loader.classList.add('hidden');
  }

  /**
   * Scroll to the top of the calculator block.
   */
  scrollToCalculator() {
    const scrollOffset = 140;
    const top = this.element.getBoundingClientRect().top + window.scrollY - scrollOffset;

    window.scrollTo({
      top,
      behavior: 'smooth',
    });
  }

  /**
   * Repeat the form
   */
  repeatForm() {
    const [arrow, loader] = this.submitButtonTarget.children;
    arrow.classList.remove('hidden');
    loader.classList.add('hidden');

    this.resultTargets.forEach((result) => result.classList.add('hidden'));
    this.stepOneTargets.forEach((result) => result.classList.remove('hidden'));
  }

  /**
   * Select amount of persons
   *
   * @param {Event} e
   */
  selectPersons(e) {
    const value = +e.target.value;
    if (value === 1) {
      this.personTwoTarget.classList.add('hidden');
      this.personTwoInputTarget.value = null;
      this.personTwoInputTarget.required = false;
    } else {
      this.personTwoTarget.classList.remove('hidden');
      this.personTwoInputTarget.required = true;
    }
  }

  /**
   * Change incomeType
   *
   * @param {Event} e
   */
  changeIncomeType(e) {
    this.incomeTypeValue = e.target.value;

    if (this.hasMonthlyIncomeLabelTarget) {
      if (this.incomeTypeValue === 'month') {
        this.monthlyIncomeLabelTarget.innerHTML = 'Wat is je bruto inkomen per maand?';
      }

      if (this.incomeTypeValue === 'week') {
        this.monthlyIncomeLabelTarget.innerHTML = 'Wat is je bruto inkomen per 4 weken?';
      }
    }
  }

  /**
   * Validate form
   *
   * @return {Boolean}
   */
  validateForm() {
    let isValid = true;

    const requiredFieldSelectors = 'input:required';
    const requiredFields = this.formTarget.querySelectorAll(requiredFieldSelectors);

    requiredFields.forEach((field) => {
      if (!field.disabled && !field.value.trim()) {
        isValid = false;
      }
    });

    return isValid;
  }

  /**
   * Show and populate email form
   *
   * @param {Event} e
   */
  showEmailForm(e) {
    if (this.modalController) {
      this.modalController.show(e.params.form, e.params.portal, this.onEmailFormReady.bind(this));
    } else {
      throw Error('Modal controller not found');
    }
  }

  /**
   * On email form ready.
   *
   * @param {Object} form
   */
  onEmailFormReady(form) {
    form.querySelector('input[name="berekende_maximale_hypotheek"]').value = this.currencyFormat.format(this.mortgage);
    form.querySelector('input[name="ingevoerd_jaarinkomen"]').value = this.currencyFormat.format(+this.personOneInputTarget.value);
    form.querySelector('input[name="ingevoerd_jaarinkomen_partner"]').value = this.currencyFormat.format(+this.personTwoInputTarget.value);

    const hsContext = form.querySelector('input[name="hs_context"]');
    if (hsContext) {
      const hsContextObject = JSON.parse(hsContext.value);
      hsContextObject.pageUrl = window.location.href;
      hsContextObject.pageName = document.title;
      hsContext.value = JSON.stringify(hsContextObject);
    }

    const location = form.querySelector('input[name="pagelocation"]');
    if (location) {
      location.value = window.location.href;
    }

    const monthlyCostsGross = form.querySelector('input[name="berekende_bruto_maandlasten"]');
    if (monthlyCostsGross) {
      monthlyCostsGross.value = this.currencyFormat.format(this.monthlyCostsGross);
    }

    const netMontlyCostsNett = form.querySelector('input[name="berekende_netto_maandlasten"]');
    if (netMontlyCostsNett) {
      netMontlyCostsNett.value = this.currencyFormat.format(this.monthlyCostsNett);
    }
  }

  /**
   * Calculate yearly income
   *
   * @param {Event} event
   */
  calculateYearlyIncome(event) {
    const incomeTarget = this.targets.find(`${event.params.target}Income`);
    if (this.incomeTypeValue === 'month') {
      this.targets.find(event.params.target).value = (
        +incomeTarget.value * 12 * this.holidayAllowance
      ).toFixed(2);
    }

    if (this.incomeTypeValue === 'week') {
      this.targets.find(event.params.target).value = (
        +incomeTarget.value * 13 * this.holidayAllowance
      ).toFixed(2);
    }

    this.closeModal(event.params.id);
  }

  /**
   * Close modal
   *
   * @param {String} id
   */
  closeModal(id) {
    const modal = document.getElementById(`modal-${id}`);
    const controller = this.application.getControllerForElementAndIdentifier(modal, 'modal');

    if (controller) {
      controller.close();
    } else {
      throw Error('Modal controller not found');
    }
  }
}
